import dayjs from "dayjs";
import { Card } from "../../UI/Card.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  CloserDetailDTO,
  CloserRankingDTO,
} from "../../HubSpot/useGetSchedulerCloserDetail.ts";
import { SchedulerCloserDTO } from "../../HubSpot/useGetSchedulerClosers.ts";
import { formatRate } from "../Stats/closerStatsFormat.ts";
import { tierBadgeClassName, tierLabel } from "./closerTierColors.ts";

function positionOnScale(cr: number, min: number, max: number): number {
  const range = max - min;
  if (range < 0.0001) return 50;
  return ((cr - min) / range) * 100;
}

function CrDistribution({
  ranking,
  closerId,
}: {
  ranking: CloserRankingDTO;
  closerId: string;
}) {
  if (ranking.closers.length === 0) return null;
  const crs = ranking.closers.map((c) => c.cr);
  const lowest = Math.min(...crs);
  const highest = Math.max(...crs);
  const leftOf = (cr: number) => `${positionOnScale(cr, lowest, highest)}%`;
  const current = ranking.closers.find((c) => c.closerId === closerId);

  return (
    <div className="flex flex-col gap-1">
      <div className="relative h-5 mx-1.5">
        <div className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
        {ranking.closers
          .filter((c) => c.closerId !== closerId)
          .map((c) => (
            <span
              key={c.closerId}
              title={`${c.name} · ${formatRate(c.cr)}`}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-slate-400/40"
              style={{ left: leftOf(c.cr) }}
            />
          ))}
        {current && (
          <span
            title={`${current.name} · ${formatRate(current.cr)}`}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-slate-800 ring-2 ring-white"
            style={{ left: leftOf(current.cr) }}
          />
        )}
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span>{formatRate(lowest)}</span>
        <span>{formatRate(highest)}</span>
      </div>
    </div>
  );
}

function windowLabel(crWindowDays: number): string {
  const today = dayjs().tz("Europe/Rome");
  const from = today.subtract(crWindowDays, "day").format("DD/MM");
  const to = today.subtract(1, "day").format("DD/MM");
  return `dal ${from} al ${to}`;
}

function nextTierText(detail: CloserDetailDTO, closer: SchedulerCloserDTO) {
  if (closer.inTrial && closer.bookableFrom) {
    const trialEnd = dayjs(closer.bookableFrom)
      .add(detail.trialDays - 1, "day")
      .format("DD/MM/YYYY");
    return `In prova fino al ${trialEnd}: fino ad allora il tier è Tier Prova`;
  }
  if (!detail.nextTier) {
    return closer.tierRank === 1
      ? "Tier più alto raggiunto"
      : "Nessuna call effettuata nella finestra del CR";
  }
  const deals = detail.nextTier.dealsNeeded;
  return `Per il ${detail.nextTier.tier} (${formatRate(detail.nextTier.minCr)}) ${deals === 1 ? "manca 1 deal" : `mancano ${deals} deal`} a parità di call`;
}

export function CloserCrCard({
  detail,
  closer,
}: {
  detail: CloserDetailDTO;
  closer: SchedulerCloserDTO;
}) {
  const tier = tierLabel(closer.tierRank, closer.inTrial);
  const { ranking } = detail;

  return (
    <Card>
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
        CR attuale
      </h2>
      <div className="flex flex-wrap items-center justify-between gap-8">
        <div className="flex flex-wrap items-center gap-8">
          <div className="flex flex-col items-start gap-1.5">
            <span className="text-xs font-medium text-slate-500">
              CR dello scheduler
            </span>
            <div
              className={classNames(
                "flex flex-col items-center px-5 py-2.5 rounded-2xl",
                tierBadgeClassName(tier),
              )}
            >
              <span className="text-4xl font-bold tabular-nums">
                {formatRate(closer.cr)}
              </span>
              {tier && <span className="text-xs font-semibold">{tier}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 border-l border-slate-100 pl-8">
            <span className="text-xs text-slate-500">
              Ultimi {detail.crWindowDays} giorni ·{" "}
              {windowLabel(detail.crWindowDays)}
            </span>
            <span className="text-2xl font-semibold tabular-nums">
              {closer.crWonDeals ?? "—"}{" "}
              <span className="text-sm font-normal text-slate-500">
                deal vinti su
              </span>{" "}
              {closer.crCallsDone ?? "—"}{" "}
              <span className="text-sm font-normal text-slate-500">
                call effettuate
              </span>
            </span>
            <span className="text-xs text-slate-400">
              {nextTierText(detail, closer)}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2 min-w-64 flex-1 max-w-md">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-slate-500">
              CR fra i closer prenotabili, senza quelli in prova
            </span>
            <span className="text-xs text-slate-500 whitespace-nowrap">
              {ranking.position ? (
                <>
                  <span className="text-sm font-bold text-slate-800">
                    #{ranking.position}
                  </span>{" "}
                  su {ranking.closers.length}
                </>
              ) : (
                "fuori classifica"
              )}
            </span>
          </div>
          <CrDistribution ranking={ranking} closerId={closer.closerId} />
          <span className="text-xs text-slate-400">
            CR del team: {formatRate(ranking.teamCr)}
          </span>
        </div>
      </div>
    </Card>
  );
}
